import React, { useState, useEffect, useContext, useMemo } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Colorize } from "@material-ui/icons";
import { ColorBox } from 'material-ui-color';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { useModuleAccess } from "../../context/ModuleAccess/ModuleAccessContext";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	colorAdorment: {
		width: 20,
		height: 20,
	},
}));

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Mensagem muito curta")
		.required("Obrigatorio"),
	kanbanOrder: Yup.number()
		.transform((value, originalValue) => originalValue === "" || originalValue === null ? 0 : value)
		.integer("Informe um numero inteiro")
		.min(0, "Informe um numero maior ou igual a zero")
});

const TagModal = ({ open, onClose, tagId, kanban }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isKanban = Number(kanban) === 1;
	const isAdmin = user?.profile === "admin";
	const moduleAccess = useModuleAccess();
	const canAccessKanban = !moduleAccess.loading && !moduleAccess.error && moduleAccess.features.kanban?.allowed === true;
	const canEditKanban = !isKanban || (isAdmin && canAccessKanban);
	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [lanes, setLanes] = useState([]);
	const [selectedLane, setSelectedLane] = useState(null);
	const [selectedRollbackLane, setSelectedRollbackLane] = useState(null);


	const initialState = useMemo(() => ({
		name: "",
		color: getRandomHexColor(),
		kanban: kanban,
		kanbanOrder: 0,
		timeLane: 0,
		nextLaneId: 0,
		greetingMessageLane: "",
		rollbackLaneId: 0,
	}), [kanban]);

	const [tag, setTag] = useState(initialState);

	useEffect(() => {
		if (!open || !isKanban || !canAccessKanban) {
			setLanes([]);
			return;
		}

		let isMounted = true;

		const fetchTags = async () => {
			try {
				const { data } = await api.get("/tag/kanban/");
				if (isMounted) {
					setLanes(data.lista || []);
				}
			} catch (err) {
				if (isMounted) toastError(err);
			}
		};

		fetchTags();

		return () => {
			isMounted = false;
		};
	}, [open, isKanban, canAccessKanban]);

	useEffect(() => {
		const fetchTag = async () => {
			try {
				if (!tagId) {
					setTag(initialState);
					setSelectedLane(null);
					setSelectedRollbackLane(null);
					return;
				}

				const { data } = await api.get(`/tags/${tagId}`);
				setTag(prevState => {
					return { ...prevState, ...data };
				});
				if (data.nextLaneId && Number(data.nextLaneId) !== Number(tagId)) {
					setSelectedLane(data.nextLaneId);
				} else {
					setSelectedLane(null);
				}
				if (data.rollbackLaneId && Number(data.rollbackLaneId) !== Number(tagId)) {
					setSelectedRollbackLane(data.rollbackLaneId);
				} else {
					setSelectedRollbackLane(null);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchTag();
	}, [initialState, tagId, open]);

	const handleClose = () => {
		setTag(initialState);
		setSelectedLane(null);
		setSelectedRollbackLane(null);
		setColorPickerModalOpen(false);
		onClose();
	};

	const handleSaveTag = async values => {
		if (isKanban && !isAdmin) {
			toast.error(i18n.t("backendErrors.ERR_NO_PERMISSION"));
			return;
		}

		const nextLaneId = selectedLane ? Number(selectedLane) : null;
		const rollbackLaneId = selectedRollbackLane ? Number(selectedRollbackLane) : null;
		const currentTagId = tagId ? Number(tagId) : null;

		if (
			currentTagId &&
			(nextLaneId === currentTagId || rollbackLaneId === currentTagId)
		) {
			toast.error(i18n.t("backendErrors.ERR_KANBAN_LANE_CANNOT_REFERENCE_SELF"));
			return;
		}

		const tagData = {
			...values,
			userId: user?.id,
			kanban: kanban,
			nextLaneId,
			rollbackLaneId,
		};

		if (isKanban) {
			tagData.kanbanOrder = Number(values.kanbanOrder || 0);
		}

		try {
			if (tagId) {
				await api.put(`/tags/${tagId}`, tagData);
			} else {
				await api.post("/tags", tagData);
			}
			toast.success(kanban === 0 ? `${i18n.t("tagModal.success")}` : `${i18n.t("tagModal.successKanban")}`);
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	function getRandomHexColor() {
		// Gerar valores aleatórios para os componentes de cor
		const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
		const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
		const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255

		// Converter os componentes de cor em uma cor hexadecimal
		const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;

		return hexColor;
	}

	const availableLanes = lanes.filter((lane) => Number(lane.id) !== Number(tagId));

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{(tagId ? (kanban === 0 ? `${i18n.t("tagModal.title.edit")}` : `${i18n.t("tagModal.title.editKanban")}`) :
						(kanban === 0 ? `${i18n.t("tagModal.title.add")}` : `${i18n.t("tagModal.title.addKanban")}`))
					}
				</DialogTitle>
				<Formik
					initialValues={tag}
					enableReinitialize={true}
					validationSchema={TagSchema}
					onSubmit={async (values, actions) => {
						await handleSaveTag(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values }) => (
						<Form>
							<DialogContent dividers>
								<Grid container spacing={1}>
									{isKanban && !isAdmin && (
										<Grid item xs={12}>
											<Typography color="textSecondary">
												{i18n.t("backendErrors.ERR_NO_PERMISSION")}
											</Typography>
										</Grid>
									)}
									<Grid item xs={12} md={12} xl={12}>
										<Field
											as={TextField}
											label={i18n.t("tagModal.form.name")}
											name="name"
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											onChange={(e) => setTag(prev => ({ ...prev, name: e.target.value }))}
											disabled={!canEditKanban}
											fullWidth

										/>
									</Grid>
									<Grid item xs={12} md={12} xl={12}>
										<Field
											as={TextField}
											fullWidth
											label={i18n.t("tagModal.form.color")}
											name="color"
											autoFocus
											id="color"
											error={touched.color && Boolean(errors.color)}
											helperText={touched.color && errors.color}
											disabled={!canEditKanban}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<div
															style={{ backgroundColor: values.color }}
															className={classes.colorAdorment}
														></div>
													</InputAdornment>
												),
												endAdornment: (
													<IconButton
														size="small"
														color="default"
														disabled={!canEditKanban}
														onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
													>
														<Colorize />
													</IconButton>
												),
											}}
											variant="outlined"
											margin="dense"
										/>

										{colorPickerModalOpen && canEditKanban && (
											<div>
												<ColorBox
													disableAlpha={true}
													hslGradient={false}
													style={{ margin: '20px auto 0' }}
													value={tag.color}
													onChange={val => {
														setTag(prev => ({ ...prev, color: `#${val.hex}` }));
													}}
												/>
											</div>
										)}
									</Grid>

									{isKanban && isAdmin && (
										<>
											<Grid item xs={12} md={6} xl={6}>
												<Field
													as={TextField}
													type="number"
													label={i18n.t("tagModal.form.kanbanOrder")}
													name="kanbanOrder"
													error={touched.kanbanOrder && Boolean(errors.kanbanOrder)}
													helperText={touched.kanbanOrder && errors.kanbanOrder}
													variant="outlined"
													margin="dense"
													inputProps={{ min: 0, step: 1 }}
													onChange={(e) => setTag(prev => ({ ...prev, kanbanOrder: e.target.value }))}
													fullWidth
												/>
											</Grid>
											<Grid item xs={12} md={6} xl={6}>
												<Field
													as={TextField}
													label={i18n.t("tagModal.form.timeLane")}
													name="timeLane"
													error={touched.timeLane && Boolean(errors.timeLane)}
													helperText={touched.timeLane && errors.timeLane}
													variant="outlined"
													margin="dense"
													onChange={(e) => setTag(prev => ({ ...prev, timeLane: e.target.value }))}
													fullWidth
												/>
											</Grid>
											<Grid item xs={12} md={6} xl={6}>
												<FormControl
													variant="outlined"
													margin="dense"
													fullWidth
													className={classes.formControl}
												>
													<InputLabel id="whatsapp-selection-label">
														{i18n.t("tagModal.form.nextLaneId")}
													</InputLabel>
													<Field
														as={Select}
														label={i18n.t("tagModal.form.nextLaneId")}
														placeholder={i18n.t("tagModal.form.nextLaneId")}
														labelId="whatsapp-selection-label"
														id="nextLaneId"
														name="nextLaneId"
														style={{ left: "-7px" }}
														error={touched.nextLaneId && Boolean(errors.nextLaneId)}
														value={selectedLane || ""}
														onChange={(e) => setSelectedLane(e.target.value || null)}
													>
														<MenuItem value="">&nbsp;</MenuItem>
														{availableLanes &&
															availableLanes.map((lane) => (
																<MenuItem key={lane.id} value={lane.id}>
																	{lane.name}
																</MenuItem>
															))}
													</Field>
												</FormControl>
											</Grid>
											<Grid item xs={12} md={12} xl={12}>
												<Field
													as={TextField}
													label={i18n.t("tagModal.form.greetingMessageLane")}
													name="greetingMessageLane"
													rows={5}
													multiline
													error={touched.greetingMessageLane && Boolean(errors.greetingMessageLane)}
													helperText={touched.greetingMessageLane && errors.greetingMessageLane}
													variant="outlined"
													margin="dense"
													onChange={(e) => setTag(prev => ({ ...prev, greetingMessageLane: e.target.value }))}
													fullWidth
												/>
											</Grid>
											<Grid item xs={12} md={12} xl={12}>
												<FormControl
													variant="outlined"
													margin="dense"
													fullWidth
													className={classes.formControl}
												>
													<InputLabel id="whatsapp-selection-label">
														{i18n.t("tagModal.form.rollbackLaneId")}
													</InputLabel>
													<Field
														as={Select}
														label={i18n.t("tagModal.form.rollbackLaneId")}
														placeholder={i18n.t("tagModal.form.rollbackLaneId")}
														labelId="whatsapp-selection-label"
														id="rollbackLaneId"
														name="rollbackLaneId"
														style={{ left: "-7px" }}
														error={touched.rollbackLaneId && Boolean(errors.rollbackLaneId)}
														value={selectedRollbackLane || ""}
														onChange={(e) => setSelectedRollbackLane(e.target.value || null)}
													>
														<MenuItem value="">&nbsp;</MenuItem>
														{availableLanes &&
															availableLanes.map((lane) => (
																<MenuItem key={lane.id} value={lane.id}>
																	{lane.name}
																</MenuItem>
															))}
													</Field>
												</FormControl>
											</Grid>
										</>
									)}
								</Grid>

							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("tagModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting || !canEditKanban}
									variant="contained"
									className={classes.btnWrapper}
								>
									{tagId
										? `${i18n.t("tagModal.buttons.okEdit")}`
										: `${i18n.t("tagModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default TagModal;
